import React, { useEffect, useRef, useState } from 'react';
import { PLAYER_TRANSITION } from './constants';

/**
 * TV frame with the running-content placeholder ("LIVE" pitch) and the design
 * placed exactly where the player puts it (MEASURED player geometry):
 *   lshape  full-bleed 1920x1080 creative stretched under the content, which
 *           covers the top-left 1536x864 (LShapeGeometry, 80%)
 *   bottom  1920x216 strip, CENTER_CROP (BottomAdOverlayView BANNER_RATIO .20)
 *   corner  270x270 box, FIT_CENTER, 30px margin bottom-end (CornerAdOverlayView 180dp)
 * Same technique and CSS values as the prototype: a fixed 1920x1080 stage
 * scaled to the frame width. `src` = image URL, or `source` = a canvas whose
 * pixels are mirrored (the live template preview — the very canvas that gets
 * uploaded, so preview and PNG cannot differ).
 */
const GEO = {
  lshape: { pitch: [0, 0, 1536, 864], media: [0, 0, 1920, 1080, 'fill'], mediaAbove: false },
  bottom: { pitch: [0, 0, 1920, 864], media: [0, 864, 1920, 216, 'cover'], mediaAbove: true },
  corner: { pitch: [0, 0, 1920, 1080], media: [1620, 780, 270, 270, 'contain'], mediaAbove: true },
};

/** The area the running match keeps on the L frame, on the 1920x1080 stage: the ONE definition the preview and the upload-row overlay share. */
export const L_MATCH_AREA = GEO.lshape.pitch;
const STAGE = [1920, 1080];

/**
 * `enter` (optional): while «شاهد التسلسل» plays, true = the creative is on screen, false = off screen; the change animates exactly like the player
 * (PLAYER_TRANSITION). Undefined = static, as everywhere else. L: two windows onto the same creative (right strip slides in from the right, bottom
 * strip up from the bottom); bar: slides up; corner: fades.
 */
export default function ShapeFrame({ shape, src, source, rev = 0, ariaLabel, enter }) {
  const tvRef = useRef(null);
  const cvRef = useRef(null);
  const [scale, setScale] = useState(0.2);
  const g = GEO[shape] || GEO.lshape;

  useEffect(() => {
    const el = tvRef.current;
    if (!el) return undefined;
    const fit = () => setScale((el.clientWidth || 384) / 1920);
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cv2Ref = useRef(null); // L only, while a sequence plays: the second window (bottom strip) onto the same creative
  useEffect(() => {
    if (!source) return;
    [cvRef.current, cv2Ref.current].forEach((c) => {
      if (!c) return;
      c.width = source.width; c.height = source.height;
      c.getContext('2d').drawImage(source, 0, 0);
      const li = source.layoutInfo; // L-strip fit check: exposed on the element so a browser run can assert contentBottom <= limit
      if (li) { c.dataset.stripBottom = String(Math.round(li.contentBottom)); c.dataset.stripLimit = String(li.limit); c.dataset.stripOverflow = li.overflow ? '1' : '0'; } else { delete c.dataset.stripBottom; delete c.dataset.stripLimit; delete c.dataset.stripOverflow; }
    });
  }, [source, rev, enter, shape]);

  const [mx, my, mw, mh, fit] = g.media;
  const mediaStyle = { position: 'absolute', left: mx, top: my, width: mw, height: mh, objectFit: fit, zIndex: g.mediaAbove ? 2 : 1 };
  // player-like entrance / exit (only while a sequence plays)
  const tr = PLAYER_TRANSITION[shape] || PLAYER_TRANSITION.lshape;
  const animated = enter !== undefined;
  const on = enter !== false;
  const trans = (prop) => (animated ? { transition: `${prop} ${tr.ms}ms ${tr.ease}`, willChange: prop } : {});
  // Animated pieces get their own keys: the element is REMOUNTED off screen when a sequence starts (it never slides out of its resting place first)
  // and back to the static one when it ends.
  const piece = (extra, ref, key) => {
    const k = animated ? `anim-${key}` : key;
    return source
      ? <canvas key={k} ref={ref} className="sf-media" style={{ ...mediaStyle, ...extra }} />
      : (src ? <img key={k} className="sf-media" alt="" src={src} style={{ ...mediaStyle, ...extra }} /> : null);
  };
  let media;
  if (animated && shape === 'lshape') {
    // the stage is 1920x1080; the player's strips: right = x 1536..1920 (384 wide, y 0..864), bottom = y 864..1080 (216 high, full width)
    const [, , mwid, mhei] = L_MATCH_AREA;
    const rightW = STAGE[0] - mwid; const bottomH = STAGE[1] - mhei;
    media = (
      <>
        {piece({ clipPath: `inset(0px 0px ${bottomH}px ${mwid}px)`, transform: `translateX(${on ? 0 : rightW}px)`, ...trans('transform') }, cvRef, 'r')}
        {piece({ clipPath: `inset(${mhei}px 0px 0px 0px)`, transform: `translateY(${on ? 0 : bottomH}px)`, ...trans('transform') }, cv2Ref, 'b')}
      </>
    );
  } else if (animated && shape === 'bottom') {
    media = piece({ transform: `translateY(${on ? 0 : mh}px)`, ...trans('transform') }, cvRef, 'm');
  } else if (animated) {
    media = piece({ opacity: on ? 1 : 0, ...trans('opacity') }, cvRef, 'm');
  } else {
    media = piece({}, cvRef, 'm');
  }
  const [px, py, pw, ph] = g.pitch;

  return (
    <div className="tvframe" role="img" aria-label={ariaLabel}>
      <div className="tv" ref={tvRef}>
        <div className="stage" style={{ transform: `scale(${scale})` }} data-shape={shape}>
          <div className="pitch" style={{ left: px, top: py, width: pw, height: ph, zIndex: g.mediaAbove ? 1 : 2 }}>
            <div className="cc" /><div className="live">LIVE</div>
          </div>
          {media}
        </div>
      </div>
    </div>
  );
}
